"use client";

import { useEffect, useRef } from "react";

import { logError } from "@/lib/logError";
import {
  isIgnorableGlobalError,
  isMutedCrossOriginScriptError,
  isThirdPartyScriptError,
} from "@/lib/sentry-shared";
import { toast } from "@/lib/toast";

import { errorEventContext } from "./errorEventContext";
import {
  initialToastBackoffState,
  nextToastBackoff,
} from "./globalErrorToastBackoff";

const ERROR_MESSAGE = "An unexpected error occurred.";

/**
 * Listens for unhandled errors and promise rejections that occur outside
 * React's render cycle (event handlers, async callbacks, third-party scripts).
 * Shows a throttled sonner toast so the user knows something went wrong.
 *
 * Sentry's global handler already captures these errors — this component
 * only adds user-visible feedback via toast.
 *
 * Uses a hardcoded string instead of useTranslations because this component
 * lives outside NextIntlClientProvider and must not depend on any provider
 * that could itself be broken.
 *
 * Mount once in the root layout alongside <Toaster>.
 */
export function GlobalErrorListener() {
  const toastBackoffRef = useRef(initialToastBackoffState());

  useEffect(() => {
    const showThrottledToast = () => {
      const { show, state } = nextToastBackoff(
        toastBackoffRef.current,
        Date.now()
      );
      toastBackoffRef.current = state;
      if (show) toast.error(ERROR_MESSAGE);
    };

    const handleError = (event: ErrorEvent) => {
      // Benign browser noise (e.g. "ResizeObserver loop completed with
      // undelivered notifications") — drop before any toast/logError/Sentry.
      // Reuses Sentry's ignore list via the shared predicate so the two can't
      // drift. The string lives on `event.message`; on the rarer real-Error
      // variant it's on `event.error.message`.
      const message =
        event.message ?? (event.error as Error | undefined)?.message;
      if (isIgnorableGlobalError(message)) return;
      // Thrown by a browser extension / injected shim, not by us — Sentry
      // already drops it via `denyUrls`, so toasting and logging it here is the
      // one surface where foreign noise still reaches the user. `filename` is
      // the fallback for the stackless iOS shape.
      if (isThirdPartyScriptError(event.error, event.filename)) return;
      // iOS WebKit often delivers `event.error === null` with the detail on the
      // event itself (message + filename:line:col). Capture that context so a
      // detail-less crash like CUSTOMER-UI-3T is still diagnosable downstream.
      logError("Unhandled error", event.error, errorEventContext(event));
      // Muted cross-origin "Script error." (error === null) is unactionable
      // third-party noise where the user's action usually succeeded — log it
      // (the Mixpanel "Error Logged" event above is our only queryable trace,
      // since Sentry drops the class by default) but skip the false-alarm toast.
      // Real iOS errors also arrive with error === null but a genuine message,
      // so this only suppresses the literal muted string.
      if (isMutedCrossOriginScriptError(message, event.error)) return;
      showThrottledToast();
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason: unknown = event.reason;
      const message =
        typeof reason === "string"
          ? reason
          : (reason as Error | undefined)?.message;
      if (isIgnorableGlobalError(message)) return;
      // Same foreign-origin guard as handleError. This is the shape that hit
      // production: an extension patching XMLHttpRequest rejects on every XHR
      // completion, so each of our own telemetry posts fed the storm.
      if (isThirdPartyScriptError(reason)) return;
      logError("Unhandled promise rejection", reason);
      showThrottledToast();
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  return null;
}
